import React, { ChangeEvent, FC, useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DomainOutlinedIcon from '@mui/icons-material/DomainOutlined';
import LaptopMacOutlinedIcon from '@mui/icons-material/LaptopMacOutlined';
import { Box, MenuItem, Typography } from '@mui/material';
import useTranslation from 'next-translate/useTranslation';

import {
  OnboardingModalActions,
  useOnboardingModalActionsCompact,
} from '@/components/MainModal/OnboardingModalActions';
import { useAppDispatch, useAppSelector, useOnboardingTask, useTracking, useTraits } from '@/hooks';
import {
  selectCompanyId,
  selectHubspotContactId,
  selectUserEmail,
  selectUserId,
  setHubspotContactId,
} from '@/redux/features/authSlice';
import { setTeamInfo } from '@/redux/features/onboardingSlice';
import CompanyRequests from '@/requests/company.requests';
import HubspotRequests from '@/requests/hubspot.requests';
import { AmplitudeService } from '@/services/tracking/amplitude.service';
import { Button, LoadingButton } from '@/shared/Button';
import { TextField } from '@/shared/TextField';
import { Tooltip } from '@/shared/Tooltip';

import { StepCommonProps, StepInfo, TeamInfoStep as TeamInfoStepType } from '../../../types';
import SentryService from '../../../utils/sentry-service.util';

import { SelectionChip } from './SelectionChip';
import {
  buildTeamInfoHubSpotProperties,
  departmentOptions,
  expectedTeamSizeOptions,
  industryOptions,
  isTeamInfoComplete,
  jobLevelOptions,
  normalizeTeamInfoFormValues,
  TeamInfoFormValues,
  teamWorkStyleOptions,
  usedTimeTrackingOptions,
} from './teamInfoOptions';

import styles from './TeamInfoStep.module.scss';

const selectFieldStyles = css`
  width: 100%;

  & .MuiInputLabel-root:not(.Mui-focused, .Mui-error) {
    color: #9a918d !important;
  }

  & .MuiSelect-select {
    color: #574841;
  }

  & .MuiSelect-icon {
    color: #574841;
  }
`;

const selectInputLabelProps = { shrink: true };

const createSelectProps = (placeholder: string, menuClassName: string) => ({
  displayEmpty: true,
  renderValue: (value: unknown) => (value as string) || placeholder,
  MenuProps: {
    PaperProps: {
      className: menuClassName,
    },
  },
});

const teamWorkStyleIcons = {
  laptop: <LaptopMacOutlinedIcon fontSize="small" />,
  briefcase: <BusinessCenterOutlinedIcon fontSize="small" />,
  building: <DomainOutlinedIcon fontSize="small" />,
} as const;

const usedTimeTrackingIcons = {
  check: <CheckIcon fontSize="small" />,
  close: <CloseIcon fontSize="small" />,
} as const;

const TeamInfoStepComponent: FC<StepCommonProps> = ({ flow }) => {
  const dispatch = useAppDispatch();
  const { goToNextStep, goToPreviousStep, getNextStep } = useOnboardingTask(flow);
  const { trackOnboardingStep } = useTracking(flow);
  const { updateTraits } = useTraits();
  const companyId = useSelector(selectCompanyId);
  const hubspotContactId = useSelector(selectHubspotContactId);
  const userEmail = useSelector(selectUserEmail);
  const userId = useSelector(selectUserId);
  const { company } = useAppSelector(state => state.auth);
  const storeTeamInfo = useAppSelector(state => state.onboarding.teamInfo);
  const { t } = useTranslation('onboarding');
  const compactActions = useOnboardingModalActionsCompact();

  const backendCustom = company?.companySettings?.custom;
  const initialValues = useMemo(
    () => normalizeTeamInfoFormValues({
      expectedteamsize: storeTeamInfo.expectedteamsize || backendCustom?.expectedteamsize,
      industry: storeTeamInfo.industry || backendCustom?.industry,
      teamworkstyle: storeTeamInfo.teamworkstyle || backendCustom?.teamworkstyle,
      userrole: storeTeamInfo.userrole || backendCustom?.userrole,
      department: storeTeamInfo.department || backendCustom?.department,
      usedtimetrackingbefore: storeTeamInfo.usedtimetrackingbefore || backendCustom?.usedtimetrackingbefore,
    }, t),
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const [formValues, setFormValues] = useState<TeamInfoFormValues>(initialValues);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasTrackedSubmission = useRef(false);
  const lastSyncedHubspotTeamInfo = useRef<string | null>(null);

  const isComplete = isTeamInfoComplete(formValues);

  const selectPlaceholder = t('team_info_step.select_placeholder');
  const selectProps = useMemo(
    () => createSelectProps(selectPlaceholder, styles.SelectMenu),
    [selectPlaceholder],
  );

  const persistField = useCallback((field: keyof TeamInfoFormValues, value: string) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
    dispatch(setTeamInfo({ [field]: value }));

    CompanyRequests.putCompanySettings(companyId, {
      custom: { [field]: value },
    });
  }, [companyId, dispatch]);

  const handleSelectChange = (field: keyof TeamInfoFormValues) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      persistField(field, event.target.value);
    };

  const handleChipSelect = (field: keyof TeamInfoFormValues, value: string) => {
    persistField(field, value);
  };

  const syncHubspotTeamInfo = useCallback(async (values: TeamInfoFormValues) => {
    if (!isTeamInfoComplete(values)) return;

    const syncKey = JSON.stringify(values);
    if (lastSyncedHubspotTeamInfo.current === syncKey) return;

    const teamInfoProperties = buildTeamInfoHubSpotProperties(values);
    let resolvedContactId = hubspotContactId;

    try {
      if (resolvedContactId) {
        lastSyncedHubspotTeamInfo.current = syncKey;
        await HubspotRequests.updateContactProperties(resolvedContactId, teamInfoProperties);
        return;
      }

      if (!userEmail) return;

      resolvedContactId = (await HubspotRequests.getContactIdByEmail(userEmail, 2)) ?? undefined;

      if (resolvedContactId) {
        lastSyncedHubspotTeamInfo.current = syncKey;
        await HubspotRequests.updateContactProperties(resolvedContactId, teamInfoProperties);
        dispatch(setHubspotContactId(resolvedContactId));
        return;
      }

      resolvedContactId = (await HubspotRequests.createContactByEmail(userEmail, {
        tdcompanyid: companyId,
        tduserid: userId,
        ...teamInfoProperties,
      })) ?? undefined;

      if (!resolvedContactId) return;

      lastSyncedHubspotTeamInfo.current = syncKey;
      dispatch(setHubspotContactId(resolvedContactId));
    } catch (error) {
      lastSyncedHubspotTeamInfo.current = null;
      await SentryService.captureExceptionWithRetryAfter(error, {
        errorContext: 'Failed to sync HubSpot team info',
        contactId: resolvedContactId,
      });
    }
  }, [companyId, dispatch, hubspotContactId, userEmail, userId]);

  const trackTeamInfoSubmission = useCallback(() => {
    if (hasTrackedSubmission.current || !isComplete) return;

    trackOnboardingStep('Submitted About Your Team', {
      expectedteamsize: formValues.expectedteamsize,
      industry: formValues.industry,
      teamworkstyle: formValues.teamworkstyle,
      userrole: formValues.userrole,
      department: formValues.department,
      usedtimetrackingbefore: formValues.usedtimetrackingbefore,
    });

    hasTrackedSubmission.current = true;
  }, [formValues, isComplete, trackOnboardingStep]);

  const handleNextClick = async () => {
    if (isSubmitting || !isComplete) return;

    setIsSubmitting(true);

    await updateTraits({
      companyTraits: {
        expectedTeamSize: formValues.expectedteamsize,
        teamWorkStyle: formValues.teamworkstyle,
        usedTimeTrackingBefore: formValues.usedtimetrackingbefore,
        onboarding_step: getNextStep()?.stepName,
      },
      userTraits: {
        industry: formValues.industry,
        jobtitle: formValues.userrole,
        department: formValues.department,
      },
    });

    await syncHubspotTeamInfo(formValues);

    if (companyId && formValues.expectedteamsize) {
      AmplitudeService.groupIdentify('company', companyId, {
        onboardingexpectedteamsize: formValues.expectedteamsize,
      });
    }

    trackTeamInfoSubmission();
    goToNextStep();
    setIsSubmitting(false);
  };

  return (
    <>
      <div className={ styles.TeamInfoStep }>
        <Box className={ styles.FieldRow }>
          <TextField
            select
            label={ t('team_info_step.expected_team_size_label') }
            value={ formValues.expectedteamsize }
            onChange={ handleSelectChange('expectedteamsize') }
            variant="filled"
            fullWidth
            css={ selectFieldStyles }
            InputLabelProps={ selectInputLabelProps }
            SelectProps={ selectProps }
            inputProps={{ 'data-testid': 'expected-team-size-select' }}
          >
            { expectedTeamSizeOptions.map(({ key, label }) => (
              <MenuItem key={ key } value={ label }>
                { label }
              </MenuItem>
            )) }
          </TextField>

          <TextField
            select
            label={ t('team_info_step.industry_label') }
            value={ formValues.industry }
            onChange={ handleSelectChange('industry') }
            variant="filled"
            fullWidth
            css={ selectFieldStyles }
            InputLabelProps={ selectInputLabelProps }
            SelectProps={ selectProps }
            inputProps={{ 'data-testid': 'industry-select' }}
          >
            { industryOptions.map(({ key, labelKey }) => (
              <MenuItem key={ key } value={ t(labelKey) }>
                { t(labelKey) }
              </MenuItem>
            )) }
          </TextField>
        </Box>

        <Box className={ styles.FieldGroup }>
          <Typography className={ styles.FieldLabel } component="p">
            { t('team_info_step.team_work_style_label') }
          </Typography>
          <Box className={ styles.ChipRow }>
            { teamWorkStyleOptions.map(({ key, labelKey, icon }) => {
              const label = t(labelKey);

              return (
                <SelectionChip
                  key={ key }
                  label={ label }
                  icon={ teamWorkStyleIcons[icon] }
                  selected={ formValues.teamworkstyle === label }
                  onClick={ () => handleChipSelect('teamworkstyle', label) }
                  testId={ `team-work-style-${ key }` }
                />
              );
            }) }
          </Box>
        </Box>

        <Box className={ styles.FieldRow }>
          <TextField
            select
            label={ t('team_info_step.job_level_label') }
            value={ formValues.userrole }
            onChange={ handleSelectChange('userrole') }
            variant="filled"
            fullWidth
            css={ selectFieldStyles }
            InputLabelProps={ selectInputLabelProps }
            SelectProps={ selectProps }
            inputProps={{ 'data-testid': 'job-level-select' }}
          >
            { jobLevelOptions.map(({ key, labelKey }) => (
              <MenuItem key={ key } value={ t(labelKey) }>
                { t(labelKey) }
              </MenuItem>
            )) }
          </TextField>

          <TextField
            select
            label={ t('team_info_step.department_label') }
            value={ formValues.department }
            onChange={ handleSelectChange('department') }
            variant="filled"
            fullWidth
            css={ selectFieldStyles }
            InputLabelProps={ selectInputLabelProps }
            SelectProps={ selectProps }
            inputProps={{ 'data-testid': 'department-select' }}
          >
            { departmentOptions.map(({ key, labelKey }) => (
              <MenuItem key={ key } value={ t(labelKey) }>
                { t(labelKey) }
              </MenuItem>
            )) }
          </TextField>
        </Box>

        <Box className={ styles.FieldGroup }>
          <Typography className={ styles.FieldLabel } component="p">
            { t('team_info_step.used_time_tracking_label') }
          </Typography>
          <Box className={ styles.ChipRow }>
            { usedTimeTrackingOptions.map(({ key, labelKey, icon }) => {
              const label = t(labelKey);

              return (
                <SelectionChip
                  key={ key }
                  label={ label }
                  icon={ usedTimeTrackingIcons[icon] }
                  selected={ formValues.usedtimetrackingbefore === label }
                  onClick={ () => handleChipSelect('usedtimetrackingbefore', label) }
                  testId={ `used-time-tracking-${ key }` }
                />
              );
            }) }
          </Box>
        </Box>
      </div>

      <OnboardingModalActions>
        <Box
          className={ styles.Actions }
          sx={ compactActions ? { pt: 0 } : { pt: 3 } }
        >
          <Button
            variant="outlined"
            size="medium"
            uppercase
            onClick={ () => goToPreviousStep() }
          >
            { t('common:back') }
          </Button>

          <Tooltip
            title={ t('team_info_step.next_disabled_tooltip') }
            placement="top"
            disableFocusListener
            disableHoverListener={ isComplete }
            enterTouchDelay={ 0 }
            tooltipSx={{
              backgroundColor: '#30302F',
              color: '#FFFFFF',
            }}
          >
            <Box>
              <LoadingButton
                variant="contained"
                type="button"
                disableElevation={ false }
                disabled={ !isComplete || isSubmitting }
                loading={ isSubmitting }
                onClick={ handleNextClick }
                css={ css`
                  margin-bottom: 4px;
                  &.Mui-disabled {
                    cursor: not-allowed;
                    pointer-events: auto;
                  }
                ` }
              >
                { t('common:next') }
              </LoadingButton>
            </Box>
          </Tooltip>
        </Box>
      </OnboardingModalActions>
    </>
  );
};

export const TeamInfoStepInfo: StepInfo<TeamInfoStepType> = {
  Component: TeamInfoStepComponent,
  id: 'teamInfoStep',
  stepName: 'About Your Team Screen',
  title: 'onboarding:team_info_step.title',
  desc: 'onboarding:team_info_step.desc',
};
